/**
 * PhaseGuard Component
 * 
 * Wraps routes/components to show PhaseBlocked message if feature is not in current phase.
 * This is a SOFT guard - it shows a friendly message, not a hard redirect.
 * Role-based access is still checked by ProtectedRoute separately.
 * 
 * IMPORTANT: parkProAdmin users bypass all phase restrictions.
 */

import React from 'react';
import { useLocation } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { isParkProAdmin } from '../../utils/roleUtils';
import { isRouteInPhase, getRoutePhase } from '../../config/rolloutConfig';
import PhaseBlocked from './PhaseBlocked';

/**
 * PhaseGuard - Soft phase-based access control
 * @param {React.ReactNode} children - Component to render if phase allows
 * @param {string|null} route - Optional route path (defaults to current location)
 * @param {string} featureName - Optional feature name for blocked message
 */
const PhaseGuard = ({ children, route = null, featureName = null }) => {
  // Hooks must be called at the top level, not inside try-catch
  const location = useLocation();
  const { user } = useUser();
  
  try {
    const currentRoute = route || (location?.pathname || '/');
    
    // parkProAdmin users bypass all phase restrictions
    const isAdmin = isParkProAdmin(user?.role);
    
    // Check if route is available in current phase (or if admin bypasses)
    const isAvailable = isAdmin ? true : isRouteInPhase(currentRoute);
    const requiredPhase = getRoutePhase(currentRoute);
    
    // If route is available, render children (ensure children is valid)
    if (isAvailable) {
      return children ? <>{children}</> : null;
    }
    
    // If route is blocked, show PhaseBlocked message
    // Extract feature name from route if not provided
    const displayName = featureName || getFeatureNameFromRoute(currentRoute);
    
    return (
      <PhaseBlocked 
        requiredPhase={requiredPhase || 'PHASE_1'}
        featureName={displayName}
        showBackButton={true}
      />
    );
  } catch (error) {
    // If there's an error (e.g., route parsing issues), render children
    console.warn('PhaseGuard error:', error);
    return children ? <>{children}</> : null;
  }
};

/**
 * Extract a friendly feature name from route path
 * @param {string} route - Route path
 * @returns {string} Friendly feature name
 */
const getFeatureNameFromRoute = (route) => {
  // Remove leading slash and split
  const parts = route.replace(/^\//, '').split('/');
  
  // Get the last meaningful part
  const lastPart = parts[parts.length - 1];
  
  // Convert to title case
  const titleCase = lastPart
    .replace(/-/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
  
  // Special cases
  if (route.includes('/admin/')) {
    return 'Agency Admin Feature';
  }
  if (route.includes('/crm/')) {
    return 'CRM Feature';
  }
  
  return titleCase || 'This Feature';
};

export default PhaseGuard;
